import { logger } from '../../utils/logger.js'
import { Capacitor } from '@capacitor/core'

const log = logger.create('cotrex-search')

const TILE_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const INDEX_URL = `${TILE_ORIGIN}/data/cotrex/cotrex-search-index.json`

export interface CotrexSearchEntry {
  n: string // name
  t: string // trail_num
  b: [number, number, number, number] // bbox [minLng, minLat, maxLng, maxLat]
  c: [number, number] // centroid [lat, lng]
  m: string // manager
  u: string // allowed_uses
  l: number // total_length_mi
  p?: number // place_id
}

export interface CotrexSearchResult {
  entry: CotrexSearchEntry
  score: number
}

class CotrexSearchService {
  private index: CotrexSearchEntry[] | null = null
  private loading: Promise<void> | null = null

  async ensureLoaded(): Promise<void> {
    if (this.index) return
    if (this.loading) return this.loading

    this.loading = (async () => {
      try {
        const resp = await fetch(INDEX_URL)
        if (!resp.ok) {
          log.warn(`Failed to load COTREX search index: HTTP ${resp.status}`)
          this.index = []
          return
        }
        this.index = await resp.json()
        log.info(`COTREX search index loaded: ${this.index!.length} entries`)
      } catch (err) {
        log.warn('Failed to load COTREX search index:', err)
        this.index = []
      } finally {
        this.loading = null
      }
    })()

    return this.loading
  }

  async findByName(name: string): Promise<CotrexSearchEntry | null> {
    await this.ensureLoaded()
    if (!this.index) return null
    const lower = name.toLowerCase()
    return this.index.find(e => e.n.toLowerCase() === lower) ?? null
  }

  search(query: string, limit = 5): CotrexSearchResult[] {
    if (!this.index || this.index.length === 0) return []

    const lower = query.toLowerCase().trim()
    if (lower.length < 2) return []

    const tokens = lower.split(/\s+/)
    const results: CotrexSearchResult[] = []

    for (const entry of this.index) {
      const name = entry.n.toLowerCase()
      let score = 0

      if (name === lower) {
        score = 100
      } else if (name.startsWith(lower)) {
        score = 80
      } else {
        for (const token of tokens) {
          if (name.includes(token)) score += 10
        }
      }

      if (score > 0) {
        results.push({ entry, score })
      }
    }

    results.sort((a, b) => b.score - a.score)
    return results.slice(0, limit)
  }
}

export const cotrexSearchService = new CotrexSearchService()
